"use client";

import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/auth-context";
import { useEffect, useState } from "react";
import { FullPageLoading } from "@/components/ui/loading";
import { Button } from "@/components/ui/button";
import ChatComponent from "@/components/chat/ChatComponent";
import { ConnectAccountPopUp } from "@/components/common/ConnectAccountPopUp";
import { useShowConnectAccount } from "@/hooks/useShowConnectAccount";
import { useInitialMessage } from "@/hooks/useInitialMessage";

type AuthStatus = "pending" | "success" | "error";

/**
 * Renders the main chat page.
 * This component acts as the primary entry point for the chat feature. It ensures a valid
 * user session exists before rendering the chat interface. If the AuthContext initializes
 * and finds no user, this component triggers the creation of a new anonymous session.
 */
export default function ChatPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const { isInitialized, user } = useAuth();
  const { error: initialMessageError } = useInitialMessage();
  const { shouldShow } = useShowConnectAccount();
  const [popupClosed, setPopupClosed] = useState(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("pending");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const showConnectColumn = shouldShow && !popupClosed;

  const handleClose = () => {
    setPopupClosed(true);
  };

  // Effect for ensuring a user session exists.
  useEffect(() => {
    // 1. Wait for the AuthContext to be fully initialized.
    if (!isInitialized) {
      setAuthStatus("pending");
      return;
    }

    // 2. If the AuthContext has found a user (logged-in, mock, or restored anonymous), we're ready.
    if (user) {
      setAuthStatus("success");
      return;
    }

    // 3. If AuthContext is initialized and there's NO user, redirect to login.
    router.push("/login");
  }, [isInitialized, user, router]);

  // Handle initial message errors
  useEffect(() => {
    if (initialMessageError) {
      setErrorMessage(initialMessageError.message);
      setAuthStatus("error");
    }
  }, [initialMessageError]);

  if (authStatus === "pending") {
    return <FullPageLoading text={t("loading", "Loading...")} />;
  }

  if (authStatus === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("error_generic_title", "Unable to start chat")}
          </h1>
          <p className="text-muted-foreground">{errorMessage}</p>
        </div>
        <Button onClick={() => router.push("/")}>
          {t("go_home", "Go back home")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-1 bg-background lg:flex-row min-h-0">
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        <ChatComponent />
      </div>
      {/* Desktop: show beside, Mobile: show as dialog overlay */}
      {showConnectColumn && (
        <>
          {/* Desktop (lg+) */}
          <div className="hidden h-full lg:block w-[450px] p-4 overflow-y-auto">
            <ConnectAccountPopUp onClose={handleClose} />
          </div>
          {/* Mobile (below lg) */}
          <div className="lg:hidden">
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
              onClick={handleClose}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md p-4"
              >
                <ConnectAccountPopUp onClose={handleClose} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
